/**
 * socle-compat.ts — the compatibility matrix of the two halves of the socle.
 *
 * A SmartStack app runs the backend package `SmartStack` (NuGet) and the
 * frontend package `@atlashub/smartstack` (npm). They are released together
 * and speak one contract (routes, permissions, DTOs, i18n namespaces):
 *
 * | NuGet vs npm            | Verdict | Why                                             |
 * |-------------------------|---------|-------------------------------------------------|
 * | same version            | ok      |                                                 |
 * | same major.minor, patch | warn    | a patch is a fix on one side, contract unchanged |
 * | major or minor differs  | err     | the contract may differ — pages, routes, DTOs    |
 * | one side unknown        | unknown | nothing to compare (no frontend, tag, range…)    |
 *
 * Used by `ss upgrade` (preflight: the npm target follows the NuGet target),
 * and by `ss doctor` (Cycle de vie block).
 */
import { parseVersion } from './smartstack-version.js';

export type SocleCompatLevel = 'ok' | 'warn' | 'err' | 'unknown';

export interface SocleCompatResult {
  level: SocleCompatLevel;
  nuget: string | null;
  npm: string | null;
  message: string;
}

/** Strip an npm range prefix (`^3.70.0`, `~3.70.0`, `=3.70.0`) to its version. */
export function normalizeNpmVersion(spec: string | null | undefined): string | null {
  if (!spec) return null;
  const cleaned = spec.trim().replace(/^[\^~=v]+/, '');
  return parseVersion(cleaned) ? cleaned : null;
}

export function checkSocleCompat(nuget: string | null | undefined, npm: string | null | undefined): SocleCompatResult {
  const n = parseVersion(nuget ?? null);
  const m = parseVersion(normalizeNpmVersion(npm ?? null));
  const nugetV = n ? (nuget as string).trim() : null;
  const npmV = m ? normalizeNpmVersion(npm ?? null) : null;
  if (!n || !m) {
    return {
      level: 'unknown',
      nuget: nugetV,
      npm: npmV,
      message: !n ? 'NuGet SmartStack version unknown' : 'npm @atlashub/smartstack version unknown',
    };
  }
  if (n.major !== m.major || n.minor !== m.minor) {
    return {
      level: 'err',
      nuget: nugetV,
      npm: npmV,
      message: `NuGet SmartStack ${nugetV} and npm @atlashub/smartstack ${npmV} differ in major.minor — the backend/frontend contract may not match. Run ss upgrade.`,
    };
  }
  if (n.patch !== m.patch || n.prerelease !== m.prerelease) {
    return {
      level: 'warn',
      nuget: nugetV,
      npm: npmV,
      message: `NuGet SmartStack ${nugetV} and npm @atlashub/smartstack ${npmV} differ in patch only — compatible, align them at the next upgrade.`,
    };
  }
  return { level: 'ok', nuget: nugetV, npm: npmV, message: `NuGet and npm on ${nugetV}` };
}
